export type CodeTone = 'plain' | 'keyword' | 'string' | 'comment' | 'muted'

export interface CodeSegment {
  text: string
  tone?: CodeTone
}

export interface CodeLine {
  segments: Array<CodeSegment>
  /** The one line the reader has to change. */
  highlight?: boolean
}

export interface CodeSampleTab {
  id: string
  label: string
  lines: Array<CodeLine>
}

const s = (text: string, tone?: CodeTone): CodeSegment => ({ text, tone })
const line = (...segments: Array<CodeSegment>): CodeLine => ({ segments })
const hl = (...segments: Array<CodeSegment>): CodeLine => ({
  segments,
  highlight: true,
})
const blank = line()

export const API_BASE_URL = 'https://api.modelstack.cc/v1'

/** Mirrors the snippets in docs/quickstart so the two never disagree. */
export const codeSampleTabs: Array<CodeSampleTab> = [
  {
    id: 'python',
    label: 'Python',
    lines: [
      line(s('from', 'keyword'), s(' openai '), s('import', 'keyword'), s(' OpenAI')),
      blank,
      line(s('client = OpenAI(')),
      line(s('    api_key='), s('"sk_..."', 'string'), s(',')),
      hl(
        s('    base_url='),
        s(`"${API_BASE_URL}"`, 'string'),
        s(',  '),
        s('# the only change', 'comment'),
      ),
      line(s(')')),
      blank,
      line(s('response = client.chat.completions.create(')),
      line(s('    model='), s('"claude-sonnet-4-6"', 'string'), s(',')),
      line(
        s('    messages=[{'),
        s('"role"', 'string'),
        s(': '),
        s('"user"', 'string'),
        s(', '),
        s('"content"', 'string'),
        s(': '),
        s('"Hello"', 'string'),
        s('}],'),
      ),
      line(s(')')),
    ],
  },
  {
    id: 'node',
    label: 'Node.js',
    lines: [
      line(s('import', 'keyword'), s(' OpenAI '), s('from', 'keyword'), s(' '), s("'openai'", 'string')),
      blank,
      line(s('const', 'keyword'), s(' client = '), s('new', 'keyword'), s(' OpenAI({')),
      line(s('  apiKey: '), s("'sk_...'", 'string'), s(',')),
      hl(
        s('  baseURL: '),
        s(`'${API_BASE_URL}'`, 'string'),
        s(',  '),
        s('// the only change', 'comment'),
      ),
      line(s('})')),
      blank,
      line(
        s('const', 'keyword'),
        s(' response = '),
        s('await', 'keyword'),
        s(' client.chat.completions.create({'),
      ),
      line(s('  model: '), s("'claude-sonnet-4-6'", 'string'), s(',')),
      line(
        s('  messages: [{ role: '),
        s("'user'", 'string'),
        s(', content: '),
        s("'Hello'", 'string'),
        s(' }],'),
      ),
      line(s('})')),
    ],
  },
  {
    id: 'curl',
    label: 'curl',
    lines: [
      hl(s('curl '), s(`${API_BASE_URL}/chat/completions`, 'string'), s(' \\')),
      line(s('  -H ', 'muted'), s('"Authorization: Bearer sk_..."', 'string'), s(' \\')),
      line(s('  -H ', 'muted'), s('"Content-Type: application/json"', 'string'), s(' \\')),
      line(s('  -d ', 'muted'), s("'{", 'string')),
      line(s('    "model": "claude-sonnet-4-6",', 'string')),
      line(s('    "messages": [{"role": "user", "content": "Hello"}]', 'string')),
      line(s("  }'", 'string')),
    ],
  },
]
